import * as React from 'react';

import type { IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';

interface IAssistantUserSelectionContextValue {
  selection: IAdminSelectUserSelection | null;
  setSelection: (next: IAdminSelectUserSelection | null) => void;
}

const AssistantUserSelectionContext = React.createContext<IAssistantUserSelectionContextValue | null>(null);

interface IAssistantUserSelectionProviderProps {
  children: React.ReactNode;
}

/**
 * Holds the operator's selected user for the Assistant tool. Lives on the
 * `AssistantScene` shell — which stays mounted while `<Outlet />` swaps the
 * active sub-tab — so the selection survives Memory ↔ Threads tab switches
 * instead of resetting every time the child scene unmounts.
 */
export const AssistantUserSelectionProvider = ({ children }: IAssistantUserSelectionProviderProps) => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const value = React.useMemo(() => ({ selection, setSelection }), [selection]);
  return <AssistantUserSelectionContext.Provider value={value}>{children}</AssistantUserSelectionContext.Provider>;
};

/**
 * Reads the shared Assistant user selection. Throws if called outside an
 * {@link AssistantUserSelectionProvider} so a misplaced consumer fails loudly
 * rather than silently getting a null selection that never persists.
 *
 * @returns The shared `selection` (the currently selected user, or `null`) and
 * `setSelection` to update it across every Assistant sub-tab.
 */
export const useAssistantUserSelection = (): IAssistantUserSelectionContextValue => {
  const ctx = React.useContext(AssistantUserSelectionContext);
  if (!ctx) {
    throw new Error('useAssistantUserSelection must be used within an AssistantUserSelectionProvider');
  }
  return ctx;
};
